import React, { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { META_MODULE_ENABLED } from "../../lib/features";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Send,
  Plus,
  Play,
  Pause,
  Square,
  ShieldCheck,
  QrCode,
  RefreshCw,
  Zap,
  Smartphone,
  Users,
  MessageSquare,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Bot,
  Sparkles,
  Filter,
  FileText,
} from "lucide-react";
import * as XLSX from "xlsx";
import {
  listCampaigns,
  createCampaign,
  startCampaign,
  pauseCampaign,
  stopCampaign,
  listQRInstances,
  listLeadLists,
  listTags,
  listTemplates,
  type CurrentUser,
  type Campaign,
  type QRInstance,
  type LeadListSummary,
  type Tag,
} from "../../lib/api";

interface ParsedLeadRow {
  full_name?: string;
  first_name?: string;
  last_name?: string;
  phone: string;
  email?: string;
  lead_status?: string;
  ad_name?: string;
  campaign_name?: string;
  have_you_had_an_iv_drip_treatment_before?: string;
  when_are_you_planning_to_visit_us?: string;
  best_time_for_a_call?: string;
  [key: string]: any;
}

export function CampaignsView({
  token,
  user,
  tenantId,
}: {
  token: string;
  user: CurrentUser;
  tenantId?: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const effectiveTenantId =
    user.role === "tenant"
      ? user.tenant_id
      : tenantId && tenantId !== "all"
      ? tenantId
      : user.tenant_id;

  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // --- WIZARD FORM STATE ---
  // Step 1: Identity & Instance Selection
  const [campaignName, setCampaignName] = useState("");
  const [channelType, setChannelType] = useState<"qr_whatsapp" | "meta_official">("qr_whatsapp");
  const [selectedInstanceIds, setSelectedInstanceIds] = useState<string[]>([]);

  // Step 2: Audience Selection (Import vs Existing Filter)
  const [audienceMode, setAudienceMode] = useState<"import" | "existing">("import");
  const [inlineListName, setInlineListName] = useState("");
  const [parsedLeads, setParsedLeads] = useState<ParsedLeadRow[]>([]);
  const [importFileName, setImportFileName] = useState("");
  const [rawPastedText, setRawPastedText] = useState("");
  const [isPastingCsv, setIsPastingCsv] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Existing audience filters
  const [selectedListName, setSelectedListName] = useState<string>("all");
  const [filterLeadStatus, setFilterLeadStatus] = useState<string>("all");
  const [selectedTagId, setSelectedTagId] = useState<string>("");
  const [filterSearch, setFilterSearch] = useState<string>("");

  // Step 3: Template & Smart Requirement Matching
  const [templateStrategy, setTemplateStrategy] = useState<"smart_requirement" | "manual">("smart_requirement");
  const [customMessage, setCustomMessage] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [includeOptOutStop, setIncludeOptOutStop] = useState(true);

  // Step 4: Anti-Ban & Pacing Settings
  const [pacingPerMinute, setPacingPerMinute] = useState(25);
  const [simulateTyping, setSimulateTyping] = useState(true);
  const [jitterMin, setJitterMin] = useState(2);
  const [jitterMax, setJitterMax] = useState(6);
  const [cooldownBatchSize, setCooldownBatchSize] = useState(20);
  const [cooldownSeconds, setCooldownSeconds] = useState(90);
  const [autoStartOnCreate, setAutoStartOnCreate] = useState(true);

  // Live Campaigns Query
  const campaignsQuery = useQuery({
    queryKey: ["campaigns", effectiveTenantId],
    queryFn: () => listCampaigns(token, effectiveTenantId),
    refetchInterval: 5_000,
  });

  // Instances Query
  const instancesQuery = useQuery({
    queryKey: ["qr-instances", effectiveTenantId],
    queryFn: () => listQRInstances(token, effectiveTenantId),
  });

  // Lead Lists Query
  const leadListsQuery = useQuery({
    queryKey: ["lead-lists", effectiveTenantId],
    queryFn: () => listLeadLists(token, effectiveTenantId),
  });

  // Tags Query
  const tagsQuery = useQuery({
    queryKey: ["tags", effectiveTenantId],
    queryFn: () => listTags(token, effectiveTenantId),
  });

  // Templates Query
  const templatesQuery = useQuery({
    queryKey: ["templates", effectiveTenantId],
    queryFn: () => listTemplates(token, effectiveTenantId),
  });

  const campaigns = campaignsQuery.data || [];
  const instances = instancesQuery.data || [];
  const connectedInstances = instances.filter((i) => i.status === "connected");
  const leadLists = leadListsQuery.data || [];
  const tags = tagsQuery.data || [];
  const templates = templatesQuery.data || [];

  function openWizard() {
    setIsWizardOpen(true);
    setWizardStep(1);
    const dateStr = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
    if (!campaignName) {
      setCampaignName(`Broadcast Campaign - ${dateStr}`);
    }
    if (!inlineListName) {
      setInlineListName(`Leads List - ${dateStr}`);
    }
    if (selectedInstanceIds.length === 0 && connectedInstances.length > 0) {
      setSelectedInstanceIds(connectedInstances.map((i) => i.id));
    }
  }

  function resetWizard() {
    setIsWizardOpen(false);
    setWizardStep(1);
    setCampaignName("");
    setInlineListName("");
    setParsedLeads([]);
    setImportFileName("");
    setRawPastedText("");
    setIsPastingCsv(false);
    setCustomMessage("");
    setSelectedTemplateId("");
  }

  // --- CSV / EXCEL PARSER FOR LEADS SHEET ---
  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsName = wb.SheetNames[0];
        if (!wsName) return;
        const ws = wb.Sheets[wsName];
        if (!ws) return;
        const rawJson: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });
        processParsedRows(rawJson);
      } catch (err) {
        console.error("Failed to parse file:", err);
        alert("Error parsing file. Please check that it is a valid CSV or Excel document.");
      }
    };
    reader.readAsBinaryString(file);
  }

  function handlePasteTextParse() {
    if (!rawPastedText.trim()) return;
    try {
      const wb = XLSX.read(rawPastedText, { type: "string" });
      const wsName = wb.SheetNames[0];
      if (!wsName) return;
      const ws = wb.Sheets[wsName];
      if (!ws) return;
      const rawJson: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });
      processParsedRows(rawJson);
      setIsPastingCsv(false);
    } catch (err) {
      console.error("Error parsing pasted text:", err);
      alert("Could not parse table. Make sure it has column headers like full_name, phone, email.");
    }
  }

  function processParsedRows(rows: any[]) {
    const cleanList: ParsedLeadRow[] = [];
    for (const r of rows) {
      let rawPhone =
        r.phone ||
        r.Phone ||
        r["phone_number"] ||
        r["Phone Number"] ||
        r["phone_e164"] ||
        "";
      rawPhone = String(rawPhone).trim();
      if (!rawPhone) continue;

      let digits = rawPhone.replace(/\D/g, "");
      if (digits.startsWith("00")) digits = digits.slice(2);
      if (!digits || digits.length < 8) continue;
      const phoneE164 = `+${digits}`;

      const fullName = String(r.full_name || r.name || r["Full Name"] || "").trim();
      const email = String(r.email || r.Email || "").trim();
      const leadStatus = String(r.lead_status || r.Status || "CREATED").trim();
      const adName = String(
        r.ad_name || r["ad name"] || r.campaign_name || r["Campaign Name"] || ""
      ).trim();

      const leadItem: ParsedLeadRow = {
        full_name: fullName,
        phone: phoneE164,
        email: email || undefined,
        lead_status: leadStatus,
        ad_name: adName || undefined,
        ...r,
      };

      cleanList.push(leadItem);
    }

    setParsedLeads(cleanList);
  }

  // --- MUTATIONS: CREATE, START, PAUSE, STOP ---
  const createCampaignMutation = useMutation({
    mutationFn: () =>
      createCampaign(
        token,
        {
          name: campaignName.trim(),
          channel_type: channelType,
          channel_instance_id: selectedInstanceIds.length > 0 ? selectedInstanceIds[0] : null,
          channel_instance_ids: selectedInstanceIds,
          lead_list_name:
            audienceMode === "import"
              ? inlineListName.trim()
              : selectedListName !== "all"
              ? selectedListName
              : null,
          tag_id: selectedTagId || null,
          inline_leads: audienceMode === "import" ? parsedLeads : undefined,
          advance_filters:
            audienceMode === "existing"
              ? {
                  lead_status: filterLeadStatus !== "all" ? filterLeadStatus : undefined,
                  search: filterSearch.trim() || undefined,
                }
              : undefined,
          template_strategy: templateStrategy,
          message_content: {
            text: templateStrategy === "manual" ? customMessage : customMessage || "",
          },
          anti_ban_config: {
            simulate_typing: simulateTyping,
            jitter_min_seconds: jitterMin,
            jitter_max_seconds: jitterMax,
            batch_cooldown_size: cooldownBatchSize,
            batch_cooldown_seconds: cooldownSeconds,
            include_opt_out_stop: includeOptOutStop,
            rotation_strategy: "round_robin",
          },
          pacing_per_minute: pacingPerMinute,
        },
        user.tenant_id
      ),
    onSuccess: async (newCamp) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      resetWizard();
      if (newCamp?.id && autoStartOnCreate) {
        try {
          await startCampaign(token, newCamp.id);
          queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        } catch (err: any) {
          console.error("Failed to auto-start campaign:", err);
        }
      }
    },
    onError: (err: any) => {
      alert(`Failed to create campaign: ${err.message || "Unknown error"}`);
    },
  });

  const startMutation = useMutation({
    mutationFn: (campaignId: string) => startCampaign(token, campaignId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
    onError: (err: any) => {
      alert(`Failed to start campaign: ${err.message || "Unknown error"}`);
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (campaignId: string) => pauseCampaign(token, campaignId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
    onError: (err: any) => {
      alert(`Failed to pause campaign: ${err.message || "Unknown error"}`);
    },
  });

  const stopMutation = useMutation({
    mutationFn: (campaignId: string) => stopCampaign(token, campaignId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
    onError: (err: any) => {
      alert(`Failed to stop campaign: ${err.message || "Unknown error"}`);
    },
  });

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        statusFilter === "all" ? true : c.status.toLowerCase() === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  }, [campaigns, searchQuery, statusFilter]);

  const sampleLeadPreview = useMemo(() => {
    const sample = parsedLeads[0] || {
      full_name: "Khushii Mohapatra",
      ad_name: "Skin Brightening Offer",
      have_you_had_an_iv_drip_treatment_before: "no, this is my first time",
      when_are_you_planning_to_visit_us: "this_week",
      best_time_for_a_call: "afternoon",
    };

    const offer = sample.ad_name ? "our Skin Brightening Treatment" : "our exclusive treatment offer";
    const history = sample.have_you_had_an_iv_drip_treatment_before?.includes("first")
      ? "As this is your first visit, our specialist will provide a complimentary consultation tailored to your goals."
      : "Welcome back! We would love to continue your treatment journey with priority perks.";
    const timing = sample.when_are_you_planning_to_visit_us?.includes("week")
      ? "We have convenient appointment openings available this week."
      : "We would be delighted to share available consultation slots with you.";
    const callSlot = sample.best_time_for_a_call?.includes("afternoon")
      ? "Would it be convenient for our clinic manager to give you a quick 2-minute call this afternoon?"
      : "Feel free to reply here directly or let us know when is best to speak with you.";

    let text = `Hello ${sample.full_name || "there"}!\n\nThank you for your interest in ${offer}. ${history}\n\n${timing} ${callSlot}`;
    if (includeOptOutStop) {
      text += "\n\n_Reply STOP to unsubscribe_";
    }
    return text;
  }, [parsedLeads, includeOptOutStop]);

  return (
    <div className="space-y-6">
      {/* 1. Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Send className="w-6 h-6 text-emerald-600" />
            <span>Campaign Broadcast Studio</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Intelligent broadcast automation: Multi-instance anti-ban rotation, lead-requirement templates, and live controls.
          </p>
        </div>

        <button
          onClick={openWizard}
          className="btn-primary text-xs py-2 px-4 flex items-center gap-2 shadow-xs"
        >
          <Plus className="w-4 h-4" />
          <span>New Campaign</span>
        </button>
      </div>

      {/* 2. Top Summary Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-2xs">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Total Campaigns</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{campaigns.length}</div>
        </div>
        <div className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-2xs">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Active / Running</span>
          </div>
          <div className="text-xl font-bold text-emerald-600 mt-1">
            {campaigns.filter((c) => c.status === "running").length}
          </div>
        </div>
        <div className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-2xs">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Connected Nodes</div>
          <div className="text-xl font-bold text-purple-600 mt-1">
            {connectedInstances.length} <span className="text-xs font-normal text-slate-400">/ {instances.length}</span>
          </div>
        </div>
        <div className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-2xs">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Total Delivered</div>
          <div className="text-xl font-bold text-slate-900 mt-1">
            {campaigns.reduce((acc, c) => acc + (c.delivered_count || 0), 0)}
          </div>
        </div>
      </div>

      {/* 3. Toolbar & Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-2xs">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Search campaigns by name..."
            className="modern-input text-xs py-1.5 w-60"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg text-xs font-medium">
            {["all", "running", "paused", "stopped", "completed", "draft"].map((st) => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                className={`px-2.5 py-1 rounded-md capitalize transition-colors ${
                  statusFilter === st
                    ? "bg-white text-slate-900 shadow-2xs font-semibold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => campaignsQuery.refetch()}
          className="btn-secondary text-xs py-1.5 px-3 flex items-center justify-center gap-1.5 text-slate-600"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${campaignsQuery.isFetching ? "animate-spin" : ""}`} />
          <span>Sync Status</span>
        </button>
      </div>

      {/* 4. Campaigns Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {campaignsQuery.isLoading ? (
          <div className="col-span-full p-16 text-center text-slate-400 text-xs bg-white border border-slate-200 rounded-xl shadow-2xs">
            <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin mx-auto mb-2" />
            <span>Loading broadcast campaigns...</span>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="col-span-full p-12 text-center bg-white border border-slate-200 rounded-xl shadow-2xs space-y-3">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto text-emerald-600">
              <Send className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-bold text-slate-900">No campaigns found</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Follow the guided process: Select WhatsApp instances, import your leads sheet, configure anti-ban strategies, and start broadcast anytime.
            </p>
            <button onClick={openWizard} className="btn-primary text-xs py-2 px-3.5 mx-auto inline-flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              <span>Create First Campaign</span>
            </button>
          </div>
        ) : (
          filteredCampaigns.map((camp: Campaign) => {
            const isRunning = camp.status === "running";
            const isPaused = camp.status === "paused";
            const isStopped = camp.status === "stopped";
            const isCompleted = camp.status === "completed";

            const activePoolCount = camp.channel_instance_ids?.length || (camp.channel_instance_id ? 1 : 0);
            const total = camp.total_recipients || 1;
            const progress = Math.min(100, Math.round(((camp.sent_count || 0) / total) * 100));

            return (
              <div
                key={camp.id}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs flex flex-col justify-between hover:border-slate-300 transition-all space-y-4"
              >
                <div>
                  {/* Top Bar: Name and Status */}
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 leading-tight">{camp.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                          <Smartphone className="w-3 h-3" />
                          <span>{activePoolCount > 1 ? `${activePoolCount} Nodes Rotating` : "Single Node"}</span>
                        </span>
                        <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          <span>{camp.total_recipients || 0} Leads</span>
                        </span>
                      </div>
                    </div>

                    <span
                      className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                        isRunning
                          ? "bg-emerald-50 text-emerald-700 border-emerald-300 animate-pulse"
                          : isPaused
                          ? "bg-amber-50 text-amber-700 border-amber-300"
                          : isStopped
                          ? "bg-rose-50 text-rose-700 border-rose-300"
                          : isCompleted
                          ? "bg-blue-50 text-blue-700 border-blue-300"
                          : "bg-slate-100 text-slate-600 border-slate-200"
                      }`}
                    >
                      {camp.status}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4 space-y-1">
                    <div className="flex justify-between items-center text-[11px] text-slate-500">
                      <span>Dispatch Progress</span>
                      <span className="font-mono font-bold text-slate-800">{progress}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${
                          isStopped ? "bg-rose-500" : isCompleted ? "bg-blue-500" : "bg-emerald-500"
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Stats Counter Grid */}
                  <div className="mt-3 grid grid-cols-3 gap-1 bg-slate-50 p-2 rounded-lg text-center text-xs">
                    <div>
                      <div className="text-[10px] text-slate-400">Sent</div>
                      <div className="font-bold text-slate-800">{camp.sent_count || 0}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-400">Delivered</div>
                      <div className="font-bold text-emerald-600">{camp.delivered_count || 0}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-400">Failed / Stopped</div>
                      <div className="font-bold text-rose-500">{camp.failed_count || 0}</div>
                    </div>
                  </div>

                  {/* Strategy & Pacing Preview */}
                  <div className="mt-3 p-2.5 bg-slate-50/70 border border-slate-200/80 rounded-lg text-[11px] text-slate-600 space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Template Engine:</span>
                      <span className="font-semibold text-slate-700">
                        {camp.template_strategy === "smart_requirement" ? "⚡ Smart Lead Matcher" : "Custom Template"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Pacing & Anti-Ban:</span>
                      <span className="font-mono font-bold text-emerald-700">{camp.pacing_per_minute} msgs/min</span>
                    </div>
                  </div>
                </div>

                {/* Card Action Controls: Start / Pause / Stop */}
                <div className="pt-3 border-t border-slate-100 space-y-2">
                  <div className="flex items-center gap-2">
                    {isRunning ? (
                      <>
                        <button
                          onClick={() => pauseMutation.mutate(camp.id)}
                          disabled={pauseMutation.isPending}
                          className="btn-secondary text-xs py-1.5 px-3 flex-1 flex items-center justify-center gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                          title="Temporarily halt campaign dispatch"
                        >
                          <Pause className="w-3.5 h-3.5" />
                          <span>Pause</span>
                        </button>
                        <button
                          onClick={() => {
                            if (confirm("Are you sure you want to STOP this campaign? Remaining pending leads will be cancelled.")) {
                              stopMutation.mutate(camp.id);
                            }
                          }}
                          disabled={stopMutation.isPending}
                          className="btn-secondary text-xs py-1.5 px-3 flex items-center justify-center gap-1 text-rose-700 border-rose-300 hover:bg-rose-50"
                          title="Permanently stop campaign and cancel remaining leads"
                        >
                          <Square className="w-3.5 h-3.5 fill-rose-600 text-rose-600" />
                          <span>Stop</span>
                        </button>
                      </>
                    ) : isPaused ? (
                      <>
                        <button
                          onClick={() => startMutation.mutate(camp.id)}
                          disabled={startMutation.isPending}
                          className="btn-primary text-xs py-1.5 px-3 flex-1 flex items-center justify-center gap-1 shadow-2xs"
                        >
                          <Play className="w-3.5 h-3.5" />
                          <span>Resume</span>
                        </button>
                        <button
                          onClick={() => {
                            if (confirm("Permanently stop this campaign and cancel remaining messages?")) {
                              stopMutation.mutate(camp.id);
                            }
                          }}
                          disabled={stopMutation.isPending}
                          className="btn-secondary text-xs py-1.5 px-3 flex items-center justify-center gap-1 text-rose-700 border-rose-300 hover:bg-rose-50"
                        >
                          <Square className="w-3.5 h-3.5 fill-rose-600 text-rose-600" />
                          <span>Stop</span>
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => startMutation.mutate(camp.id)}
                        disabled={startMutation.isPending || isCompleted}
                        className="btn-primary text-xs py-1.5 px-3 flex-1 flex items-center justify-center gap-1 shadow-2xs"
                      >
                        <Play className="w-3.5 h-3.5" />
                        <span>{isStopped ? "Restart Remaining" : "Start Broadcast"}</span>
                      </button>
                    )}
                  </div>

                  <button
                    onClick={() => navigate("/admin/inbox")}
                    className="w-full text-center text-[11px] text-emerald-700 hover:text-emerald-800 font-medium py-1 px-2 rounded-md hover:bg-emerald-50 transition-colors flex items-center justify-center gap-1 border border-emerald-200/60"
                  >
                    <MessageSquare className="w-3 h-3 text-emerald-600" />
                    <span>Open in Live Inbox & Chat</span>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ========================================================================= */}
      {/* 5. 5-STEP GUIDED CAMPAIGN WIZARD MODAL                                     */}
      {/* ========================================================================= */}
      {isWizardOpen && (
        <div className="modal-backdrop" onClick={() => setIsWizardOpen(false)}>
          <div className="modal-card max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-200">
                  <Send className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">New Broadcast Campaign</h3>
                  <p className="text-[11px] text-slate-500">Step {wizardStep} of 5: Anti-ban multi-node broadcast flow</p>
                </div>
              </div>
              <button
                onClick={() => setIsWizardOpen(false)}
                className="text-slate-400 hover:text-slate-700 text-sm font-bold p-1"
              >
                ✕
              </button>
            </div>

            {/* Stepper Progress Indicator */}
            <div className="grid grid-cols-5 gap-1 my-3 text-center text-[10px] font-semibold">
              {[
                { step: 1, label: "Instances" },
                { step: 2, label: "Audience / Leads" },
                { step: 3, label: "Template Engine" },
                { step: 4, label: "Anti-Ban Settings" },
                { step: 5, label: "Launch" },
              ].map((s) => (
                <button
                  key={s.step}
                  type="button"
                  onClick={() => setWizardStep(s.step as any)}
                  className={`py-1.5 rounded border transition-colors ${
                    wizardStep === s.step
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : wizardStep > s.step
                      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                      : "bg-slate-50 text-slate-400 border-slate-200"
                  }`}
                >
                  {s.step}. {s.label}
                </button>
              ))}
            </div>

            {/* Modal Body Container with Scroll */}
            <div className="max-h-[68vh] overflow-y-auto pr-1 space-y-4">
              {/* STEP 1: CAMPAIGN NAME & INSTANCE SELECTION */}
              {wizardStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Campaign Title *</label>
                    <input
                      type="text"
                      placeholder="e.g. Skin Brightening Special Offer - Dubai Expo"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      className="modern-input w-full text-xs"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Channel Protocol *</label>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => setChannelType("qr_whatsapp")}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          channelType === "qr_whatsapp"
                            ? "bg-emerald-50 border-emerald-500 text-emerald-950 font-medium"
                            : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-bold text-xs">
                          <QrCode className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Direct WhatsApp QR Instances</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">Multi-device rotation, typing simulation & live chat sync</p>
                      </button>

                      {META_MODULE_ENABLED && (
                        <button
                          type="button"
                          onClick={() => setChannelType("meta_official")}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            channelType === "meta_official"
                              ? "bg-blue-50 border-blue-500 text-blue-950 font-medium"
                              : "border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 font-bold text-xs">
                            <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                            <span>Official Meta WABA Channel</span>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-1">Meta-approved templates with tier scaling</p>
                        </button>
                      )}
                    </div>
                  </div>

                  {channelType === "qr_whatsapp" && (
                    <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-200 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="block text-xs font-bold text-slate-800 flex items-center gap-1.5">
                          <Smartphone className="w-4 h-4 text-emerald-600" />
                          <span>Select Sending WhatsApp Numbers (Multi-Instance Rotation) *</span>
                        </label>
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <button
                            type="button"
                            onClick={() => setSelectedInstanceIds(connectedInstances.map((i) => i.id))}
                            className="text-emerald-700 hover:underline"
                          >
                            Select All
                          </button>
                          <span>•</span>
                          <button
                            type="button"
                            onClick={() => setSelectedInstanceIds([])}
                            className="text-slate-500 hover:underline"
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      {instances.length === 0 ? (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                          No WhatsApp instances found. Please pair a WhatsApp number under <strong>Instances & QR</strong> first.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {instances.map((inst: QRInstance) => {
                            const isSelected = selectedInstanceIds.includes(inst.id);
                            const isConn = inst.status === "connected";
                            const dailyLimit = inst.anti_ban?.daily_limit || 350;
                            const dailySent = inst.anti_ban?.daily_sent_count || 0;

                            return (
                              <label
                                key={inst.id}
                                className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-all ${
                                  isSelected
                                    ? "bg-white border-emerald-500 shadow-2xs"
                                    : "bg-white/60 border-slate-200 hover:bg-white"
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedInstanceIds([...selectedInstanceIds, inst.id]);
                                      } else {
                                        setSelectedInstanceIds(selectedInstanceIds.filter((id) => id !== inst.id));
                                      }
                                    }}
                                    className="w-4 h-4 accent-emerald-600 rounded"
                                  />
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-bold text-slate-800 font-mono">
                                        {inst.phone_number ? `+${inst.phone_number}` : inst.name}
                                      </span>
                                      <span
                                        className={`text-[10px] px-1.5 py-0.2 rounded font-medium ${
                                          isConn
                                            ? "bg-emerald-100 text-emerald-800"
                                            : "bg-slate-100 text-slate-600"
                                        }`}
                                      >
                                        {inst.status}
                                      </span>
                                    </div>
                                    <p className="text-[11px] text-slate-400">{inst.name || "Default Device"}</p>
                                  </div>
                                </div>

                                <div className="text-right text-[11px]">
                                  <span className="text-slate-400">Daily Quota: </span>
                                  <span className="font-mono font-bold text-slate-700">
                                    {dailySent} / {dailyLimit}
                                  </span>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}

                      <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200/80 text-[11px] text-emerald-800 flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <div>
                          <strong>Anti-Ban Multi-Node Rotation Active:</strong> Selecting multiple instances spreads message volume across numbers in a round-robin schedule. If any node reaches its daily quota, dispatch automatically fails over to the remaining numbers.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2: AUDIENCE — DIRECT IMPORT OR ADVANCE FILTER */}
              {wizardStep === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                    <button
                      type="button"
                      onClick={() => setAudienceMode("import")}
                      className={`text-xs py-1.5 px-3 rounded-lg font-bold transition-all flex items-center gap-1.5 ${
                        audienceMode === "import"
                          ? "bg-emerald-600 text-white shadow-2xs"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <Upload className="w-3.5 h-3.5" />
                      <span>Direct Import Leads (CSV / Excel / Google Sheet)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAudienceMode("existing")}
                      className={`text-xs py-1.5 px-3 rounded-lg font-bold transition-all flex items-center gap-1.5 ${
                        audienceMode === "existing"
                          ? "bg-emerald-600 text-white shadow-2xs"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <Filter className="w-3.5 h-3.5" />
                      <span>Select Existing Contacts with Advance Filter</span>
                    </button>
                  </div>

                  {audienceMode === "import" && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          Name this Imported Leads List *
                        </label>
                        <input
                          type="text"
                          value={inlineListName}
                          onChange={(e) => setInlineListName(e.target.value)}
                          placeholder="e.g. Skin Brightening Meta Leads"
                          className="modern-input w-full text-xs"
                        />
                      </div>

                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-slate-300 hover:border-emerald-500 bg-slate-50 hover:bg-emerald-50/40 rounded-xl p-5 text-center cursor-pointer transition-all"
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <FileSpreadsheet className="w-8 h-8 text-emerald-600 mx-auto mb-1.5" />
                        <h4 className="text-xs font-bold text-slate-800">
                          {importFileName ? `File Selected: ${importFileName}` : "Click to upload CSV or Excel leads sheet"}
                        </h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Supports full_name, phone, email, ad_name, and custom questionnaire columns.
                        </p>
                      </div>

                      <div className="text-right">
                        <button
                          type="button"
                          onClick={() => setIsPastingCsv(!isPastingCsv)}
                          className="text-xs text-emerald-700 hover:underline font-medium"
                        >
                          {isPastingCsv ? "Hide Text Paste Area" : "📋 Or Paste rows from Google Sheet"}
                        </button>
                      </div>

                      {isPastingCsv && (
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                          <label className="block text-xs font-semibold text-slate-700">
                            Paste Table / CSV rows directly (including headers):
                          </label>
                          <textarea
                            value={rawPastedText}
                            onChange={(e) => setRawPastedText(e.target.value)}
                            placeholder="full_name,phone,email,ad_name,when_are_you_planning_to_visit_us?\nKhushii Mohapatra,+919820109942,khushii@gmail.com,Skin Brightening Offer,this_week"
                            className="modern-input w-full text-xs font-mono h-24"
                          />
                          <button
                            type="button"
                            onClick={handlePasteTextParse}
                            className="btn-secondary text-xs py-1.5 px-3"
                          >
                            Parse Pasted Leads
                          </button>
                        </div>
                      )}

                      {parsedLeads.length > 0 && (
                        <div className="space-y-2 pt-2">
                          <div className="flex items-center justify-between text-xs font-semibold text-slate-800">
                            <span className="flex items-center gap-1.5 text-emerald-700">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              <span>{parsedLeads.length} Leads Successfully Loaded</span>
                            </span>
                            <span className="text-[11px] text-slate-400">Showing first 3 sample records:</span>
                          </div>

                          <div className="border border-slate-200 rounded-lg overflow-x-auto text-[11px] bg-white">
                            <table className="w-full text-left">
                              <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                                <tr>
                                  <th className="p-2">Name</th>
                                  <th className="p-2">Phone</th>
                                  <th className="p-2">Offer / Ad</th>
                                  <th className="p-2">First-time Visit?</th>
                                  <th className="p-2">Visit Timing</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {parsedLeads.slice(0, 3).map((lead, idx) => (
                                  <tr key={idx} className="hover:bg-slate-50">
                                    <td className="p-2 font-medium text-slate-800">{lead.full_name || "—"}</td>
                                    <td className="p-2 font-mono text-slate-600">{lead.phone}</td>
                                    <td className="p-2 text-slate-600">{lead.ad_name || "General"}</td>
                                    <td className="p-2 text-slate-600">
                                      {lead.have_you_had_an_iv_drip_treatment_before || "—"}
                                    </td>
                                    <td className="p-2 text-slate-600">
                                      {lead.when_are_you_planning_to_visit_us || "—"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {audienceMode === "existing" && (
                    <div className="space-y-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                            Lead List:
                          </label>
                          <select
                            value={selectedListName}
                            onChange={(e) => setSelectedListName(e.target.value)}
                            className="modern-input w-full text-xs"
                          >
                            <option value="all">-- All Lists --</option>
                            {leadLists.map((l: LeadListSummary) => (
                              <option key={l.list_name} value={l.list_name}>
                                {l.list_name} ({l.total_leads} leads)
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                            Lead Status Filter:
                          </label>
                          <select
                            value={filterLeadStatus}
                            onChange={(e) => setFilterLeadStatus(e.target.value)}
                            className="modern-input w-full text-xs uppercase"
                          >
                            <option value="all">-- All Statuses --</option>
                            <option value="created">CREATED</option>
                            <option value="new">NEW</option>
                            <option value="contacted">CONTACTED</option>
                            <option value="qualified">QUALIFIED</option>
                            <option value="won">WON</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                            Tag Filter:
                          </label>
                          <select
                            value={selectedTagId}
                            onChange={(e) => setSelectedTagId(e.target.value)}
                            className="modern-input w-full text-xs"
                          >
                            <option value="">-- Any Tag --</option>
                            {tags.map((t: Tag) => (
                              <option key={t.id} value={t.id}>
                                🏷️ {t.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                            Keyword / Phone Search:
                          </label>
                          <input
                            type="text"
                            placeholder="Search name or number..."
                            value={filterSearch}
                            onChange={(e) => setFilterSearch(e.target.value)}
                            className="modern-input w-full text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 3: TEMPLATE & SMART REQUIREMENT ENGINE */}
              {wizardStep === 3 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Template Strategy *
                    </label>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => setTemplateStrategy("smart_requirement")}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          templateStrategy === "smart_requirement"
                            ? "bg-emerald-50 border-emerald-500 text-emerald-950 font-medium"
                            : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-bold text-xs">
                          <Sparkles className="w-4 h-4 text-emerald-600" />
                          <span>Auto-Template by Lead Requirement</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">
                          System self-generates conversational text using lead questions (first-time vs regular, visit timing, call slot).
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() => setTemplateStrategy("manual")}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          templateStrategy === "manual"
                            ? "bg-emerald-50 border-emerald-500 text-emerald-950 font-medium"
                            : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-bold text-xs">
                          <FileText className="w-4 h-4 text-slate-600" />
                          <span>Custom Message / Manual Template</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">
                          Compose your own Spintax message or choose from pre-saved templates.
                        </p>
                      </button>
                    </div>
                  </div>

                  {templateStrategy === "smart_requirement" && (
                    <div className="p-3.5 bg-emerald-50/50 rounded-xl border border-emerald-200 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-emerald-900 flex items-center gap-1.5">
                          <Bot className="w-4 h-4 text-emerald-600" />
                          <span>Live Smart Generation Preview (Simulated for First Lead):</span>
                        </span>
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-mono">
                          Auto-Spintax Enabled
                        </span>
                      </div>

                      <div className="p-3 bg-white rounded-lg border border-emerald-200/80 text-xs font-sans text-slate-800 whitespace-pre-wrap shadow-2xs">
                        {sampleLeadPreview}
                      </div>

                      <div className="flex items-center justify-between pt-1">
                        <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeOptOutStop}
                            onChange={(e) => setIncludeOptOutStop(e.target.checked)}
                            className="w-4 h-4 accent-emerald-600 rounded"
                          />
                          <span>Append Anti-Ban Stop Opt-Out Footer (Reply STOP to unsubscribe)</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {templateStrategy === "manual" && (
                    <div className="space-y-3">
                      {templates.length > 0 && (
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                            Load From Saved Template:
                          </label>
                          <select
                            value={selectedTemplateId}
                            onChange={(e) => {
                              setSelectedTemplateId(e.target.value);
                              const t = templates.find((tpl) => tpl.id === e.target.value);
                              if (t) {
                                setCustomMessage(t.structure?.body_text || "");
                              }
                            }}
                            className="modern-input w-full text-xs"
                          >
                            <option value="">-- Choose Template --</option>
                            {templates.map((tpl) => (
                              <option key={tpl.id} value={tpl.id}>
                                {tpl.name} ({tpl.category})
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-semibold text-slate-700">Message Content *</label>
                          <div className="flex items-center gap-1 text-[10px]">
                            <button
                              type="button"
                              onClick={() => setCustomMessage((prev) => prev + " {{first_name}}")}
                              className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono"
                            >
                              {`{{first_name}}`}
                            </button>
                            <button
                              type="button"
                              onClick={() => setCustomMessage((prev) => prev + " {{ad_name}}")}
                              className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono"
                            >
                              {`{{ad_name}}`}
                            </button>
                            <button
                              type="button"
                              onClick={() => setCustomMessage((prev) => prev + " {Hello|Hi|Greetings}")}
                              className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-mono font-medium"
                            >
                              {"{Hello|Hi}"}
                            </button>
                          </div>
                        </div>
                        <textarea
                          placeholder="{Hello|Hi} {{first_name}}! We received your request for our exclusive offer. Reply STOP to opt out."
                          value={customMessage}
                          onChange={(e) => setCustomMessage(e.target.value)}
                          className="modern-input w-full text-xs h-28"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 4: ANTI-BAN & PACING SETTINGS */}
              {wizardStep === 4 && (
                <div className="space-y-4">
                  <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-semibold text-slate-700 flex items-center gap-1">
                        <Zap className="w-4 h-4 text-amber-500" /> Dispatch Pacing:
                      </span>
                      <span className="font-mono font-bold text-emerald-700">{pacingPerMinute} msgs/min</span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="60"
                      value={pacingPerMinute}
                      onChange={(e) => setPacingPerMinute(Number(e.target.value))}
                      className="w-full accent-emerald-600"
                    />
                    <div className="flex justify-between text-[10px] text-slate-400">
                      <span>5 msgs/min (Safest for new accounts)</span>
                      <span>25 msgs/min (Recommended)</span>
                      <span>60 msgs/min (Warm accounts)</span>
                    </div>
                  </div>

                  <div className="p-3.5 bg-white rounded-xl border border-slate-200 space-y-3">
                    <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-emerald-600" />
                      <span>Advanced Anti-Ban & Emulation Safeguards</span>
                    </h4>

                    <div className="space-y-2 text-xs">
                      <label className="flex items-center justify-between p-2 rounded-lg bg-slate-50 cursor-pointer">
                        <div>
                          <span className="font-semibold text-slate-700">Simulate Human Typing:</span>
                          <p className="text-[11px] text-slate-400">Sends WhatsApp presence 'composing' 2-4s before message.</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={simulateTyping}
                          onChange={(e) => setSimulateTyping(e.target.checked)}
                          className="w-4 h-4 accent-emerald-600 rounded"
                        />
                      </label>

                      <div className="p-2 rounded-lg bg-slate-50 flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-slate-700">Random Delay Jitter:</span>
                          <p className="text-[11px] text-slate-400">Randomizes wait intervals between messages.</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={jitterMin}
                            onChange={(e) => setJitterMin(Number(e.target.value))}
                            className="modern-input w-12 text-center text-xs py-1"
                          />
                          <span className="text-slate-400 text-xs">to</span>
                          <input
                            type="number"
                            min="2"
                            max="20"
                            value={jitterMax}
                            onChange={(e) => setJitterMax(Number(e.target.value))}
                            className="modern-input w-12 text-center text-xs py-1"
                          />
                          <span className="text-slate-400 text-xs">sec</span>
                        </div>
                      </div>

                      <div className="p-2 rounded-lg bg-slate-50 flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-slate-700">Micro-Batch Cooldown:</span>
                          <p className="text-[11px] text-slate-400">Rest pause after every batch of messages.</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-slate-400 text-xs">Every</span>
                          <input
                            type="number"
                            min="5"
                            max="100"
                            value={cooldownBatchSize}
                            onChange={(e) => setCooldownBatchSize(Number(e.target.value))}
                            className="modern-input w-12 text-center text-xs py-1"
                          />
                          <span className="text-slate-400 text-xs">msgs, pause</span>
                          <input
                            type="number"
                            min="10"
                            max="300"
                            value={cooldownSeconds}
                            onChange={(e) => setCooldownSeconds(Number(e.target.value))}
                            className="modern-input w-14 text-center text-xs py-1"
                          />
                          <span className="text-slate-400 text-xs">sec</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 5: REVIEW & LAUNCH CONTROLS */}
              {wizardStep === 5 && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50/70 border border-emerald-200 rounded-xl space-y-3">
                    <h4 className="text-xs font-bold text-emerald-950 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span>Campaign Configuration Summary</span>
                    </h4>

                    <div className="grid grid-cols-2 gap-2.5 text-xs">
                      <div>
                        <span className="text-slate-400 text-[11px]">Campaign Name:</span>
                        <div className="font-bold text-slate-800">{campaignName}</div>
                      </div>
                      <div>
                        <span className="text-slate-400 text-[11px]">Selected Numbers:</span>
                        <div className="font-bold text-purple-700 font-mono">
                          {selectedInstanceIds.length} Nodes Active
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-400 text-[11px]">Target Audience:</span>
                        <div className="font-bold text-emerald-700">
                          {audienceMode === "import"
                            ? `${parsedLeads.length} Imported Leads`
                            : selectedListName !== "all"
                            ? `List: ${selectedListName}`
                            : "All Consented Contacts"}
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-400 text-[11px]">Template Engine:</span>
                        <div className="font-bold text-slate-800">
                          {templateStrategy === "smart_requirement" ? "⚡ Smart Lead Matcher" : "Custom Template"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-xs font-medium text-slate-700 p-2.5 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoStartOnCreate}
                      onChange={(e) => setAutoStartOnCreate(e.target.checked)}
                      className="w-4 h-4 accent-emerald-600 rounded"
                    />
                    <span>Immediately start dispatch after creating (You can Pause/Stop at any time)</span>
                  </label>
                </div>
              )}
            </div>

            {/* Modal Bottom Navigation */}
            <div className="flex items-center justify-between pt-3 border-t border-slate-200 mt-2">
              {wizardStep > 1 ? (
                <button
                  type="button"
                  onClick={() => setWizardStep((prev) => (prev - 1) as any)}
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span>Back</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsWizardOpen(false)}
                  className="btn-secondary text-xs py-1.5 px-3"
                >
                  Cancel
                </button>
              )}

              {wizardStep < 5 ? (
                <button
                  type="button"
                  onClick={() => setWizardStep((prev) => (prev + 1) as any)}
                  disabled={
                    (wizardStep === 1 && (!campaignName.trim() || selectedInstanceIds.length === 0)) ||
                    (wizardStep === 2 && audienceMode === "import" && parsedLeads.length === 0) ||
                    (wizardStep === 3 && templateStrategy === "manual" && !customMessage.trim())
                  }
                  className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1 shadow-2xs"
                >
                  <span>Next Step</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => createCampaignMutation.mutate()}
                  disabled={createCampaignMutation.isPending}
                  className="btn-primary text-xs py-2 px-5 flex items-center gap-1.5 shadow-xs"
                >
                  {createCampaignMutation.isPending ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Creating & Dispatching...</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-3.5 h-3.5" />
                      <span>Confirm & Launch Broadcast</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
